/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect, useRef } from "react";
import { buildImageUrl } from "../utils/theme1Helpers";
import "./edit-order-modal.css";
import { createPortal } from "react-dom";
import { formatNumber } from "utils/formatNumber";
export default function EditOrderModal({ order, products = [], theme, onCancel, onSave }) {
  const [draft, setDraft] = useState(null);
  const initialized = useRef(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;

  useEffect(() => {
    document.documentElement.style.setProperty('--theme-primary', theme.primary);
  }, [theme.primary]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!order || initialized.current) return;
    initialized.current = true;

    const clean = JSON.parse(JSON.stringify(order));
    clean.details = (clean.details || []).map(d => ({
      ...d,
      itemId: d.itemId || d.ItemId,
      variantId: d.variantId || d.VariantId,
      itemName: d.itemName || d.ItemName,
      variantName: d.variantName || d.VariantName,
      colorName: d.colorName || d.ColorName,
      size: d.size || d.Size,
      quantity: Number(d.quantity || d.Quantity || 0),
      price: Number(d.price || d.Price || 0),
      discount: 0,
      imageUrl: d.imageUrl || d.ImageUrl,
      lineTotal: Number(d.lineTotal || d.LineTotal || (Number(d.quantity || d.Quantity || 0) * Number(d.price || d.Price || 0)) || 0)
    }));

    setDraft(clean);
  }, [order]);

  useEffect(() => {
    initialized.current = false;
  }, [order?.orderId]);

  // اختيار المنتج للإضافة (Selection State)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [qty, setQty] = useState(1);
  const getOptionStock = (opt) => Number(opt?.stock ?? opt?.stockQty ?? 0);
  const resolveItemStock = (item) => {
    let currentStock = Number(item?.stockQty ?? 0);
    const prod = products.find(p => Number(p.id) === Number(item?.itemId));
    if (!prod) return Math.max(0, currentStock);
    const vari = prod.variants?.find(v => Number(v.variantId) === Number(item?.variantId));
    if (!vari) return Math.max(0, currentStock);
    const opt = (vari.options || vari.sizes || []).find(o => (o.label || o.size) === item?.size);
    if (opt) currentStock = getOptionStock(opt);
    return Math.max(0, currentStock);
  };

  // دالة لإضافة المنتج بعد اختيار اللون والمقاس
  const handleAddSelection = () => {
    if (!selectedVariant || !selectedSize) return;

    setDraft(prev => {
      const details = [...(prev.details || [])];

      const index = details.findIndex(d =>
        Number(d.variantId) === Number(selectedVariant.variantId) && d.size === selectedSize.label
      );

      const maxAvailable = getOptionStock(selectedSize);
      if (maxAvailable <= 0) {
        alert("هذا الخيار نفذ من المخزون");
        return prev;
      }

      if (index !== -1) {
        const item = details[index];
        const newQty = Number(item.quantity) + Number(qty);

        if (newQty > maxAvailable) {
          alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
          return prev;
        }

        details[index] = {
          ...item,
          quantity: newQty,
          lineTotal: newQty * Number(item.price)
        };
      } else {
        if (Number(qty) > maxAvailable) {
          alert(`العفو، لا يمكنك إضافة أكثر من المتاح في المخزون (${maxAvailable} قطعة)`);
          return prev;
        }

        const finalPrice = Number(selectedVariant.finalPrice || selectedVariant.basePrice || 0);
        const basePrice = Number(selectedVariant.basePrice || 0);

        details.push({
          id: 0,
          orderId: prev.orderId,
          itemId: Number(selectedProduct.id),
          variantId: Number(selectedVariant.variantId),
          itemName: selectedProduct.name,
          variantName: selectedVariant.variantName || "",
          colorName: selectedVariant.color?.hex || selectedVariant.color?.hexCode || selectedVariant.colorHex || "",
          size: selectedSize.label || "",
          quantity: Number(qty),
          price: finalPrice,
          imageUrl: selectedVariant.imageUrl || selectedProduct.mainImage,
          stockQty: maxAvailable,
          discount: basePrice - finalPrice,
          lineTotal: finalPrice * Number(qty)
        });
      }

      return { ...prev, details };
    });

    setSelectedProduct(null);
    setSelectedVariant(null);
    setSelectedSize(null);
    setQty(1);
  };

  const updateQty = (i, delta) => {
    if (locked) return;
    setDraft(prev => {
      const details = [...prev.details];
      const item = details[i];
      if (!item) return prev;

      const q = Number(item.quantity) + delta;

      // جلب المخزون الحقيقي من قائمة المنتجات الممررة للمودال لضمان الدقة
      const currentStock = resolveItemStock(item);

      if (delta > 0 && q > currentStock) {
        alert(`وصلت للحد الأقصى المتاح في المخزون لهذه القطعة (${currentStock} قطعة)`);
        return prev;
      }

      if (q <= 0) details.splice(i, 1);
      else {
        details[i] = {
          ...item,
          quantity: q,
          stockQty: currentStock,
          lineTotal: q * Number(item.price)
        };
      }
      return { ...prev, details };
    });
  };

  const prepareOrder = () => {
    const totalAmount = draft.details.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
    return {
      ...draft,
      totalAmount,
      netAmount: totalAmount - Number(draft.discountAmount || 0),
      tenantID: Number(draft.tenantId),
      items: draft.details.map(d => ({
        itemId: Number(d.itemId),
        variantId: Number(d.variantId),
        itemName: d.itemName,
        variantName: d.variantName,
        colorName: d.colorName,
        size: d.size,
        quantity: Number(d.quantity),
        price: Number(d.price),
        imageUrl: d.imageUrl,
        stockQty: Number(d.stockQty || 0),
        discount: Number(d.discount || 0),
        lineTotal: Number(d.quantity) * Number(d.price)
      }))
    };
  };

  if (!draft) return null;
  const locked = Number(draft.isPaid) === 1;

  return (
    <div
      className="edit-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "#fff",
        backdropFilter: isMobile ? "none" : "blur(10px)",
        padding: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        zIndex: 30000,
      }}
    >
      <div
        className="edit-modal"
        style={{
          width: "100vw",
          maxWidth: "100vw",
          height: "100vh",
          maxHeight: "100vh",
          borderRadius: 0,
          border: "none",
          overflow: "hidden",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column"
        }}
      >

        {/* مودال فرعي لاختيار المقاس واللون */}
        {/* مودال تخصيص المنتج - Full Screen Overlay */}
        {selectedProduct &&
          createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100vh",
                background: "#fff",
                zIndex: 999999,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                <button
                  onClick={() => {
                    setSelectedProduct(null);
                    setSelectedVariant(null);
                    setSelectedSize(null);
                  }}
                  className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-2xl hover:bg-gray-100 transition-all"
                >
                  ×
                </button>
                <h3 className="text-lg font-black">تخصيص المنتج</h3>
                <div className="w-10"></div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 pb-32">
                {/* ====== نفس محتواك الداخلي بدون أي تغيير ====== */}

                <div className="flex gap-6 mb-8 items-center bg-gray-50 p-6 rounded-[2rem]">
                  <img
                    src={buildImageUrl(selectedVariant?.imageUrl || selectedProduct.mainImage)}
                    className="w-32 h-32 object-contain rounded-2xl bg-white shadow-sm"
                  />
                  <div>
                    <div className="font-black text-2xl mb-1">{selectedProduct.name}</div>
                    <div className="text-gray-500 mb-2">
                      {selectedVariant?.variantName || "اختر الخيارات المفضلة"}
                    </div>
                    <div className="text-2xl font-black" style={{ color: theme.primary }}>
                      {(() => {
                        const v = selectedVariant || selectedProduct.variants?.[0];
                        const base = Number(v?.price || selectedProduct.price || 0);
                        const disc = Number(v?.discount || 0);
                        const final = Number(v?.finalPrice || base - disc);
                        return formatNumber(final);
                      })()} IQD
                    </div>
                  </div>
                </div>
                {/* اختيار اللون (الـ variant) */}
                <div className="mb-6">
                  <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-widest">1. اختر اللون</label>
                  <div className="flex flex-wrap gap-3">
                    {(() => {
                      const colorGroups = {};
                      (selectedProduct.variants || []).forEach(v => {
                        const hex = v.color?.hex || v.color?.hexCode || v.colorHex || "#333";
                        if (!colorGroups[hex]) {
                          colorGroups[hex] = v; // أول فاريانت يمثل هذا اللون
                        }
                      });

                      return Object.values(colorGroups).map(v => (
                        <button
                          key={v.variantId}
                          onClick={() => {
                            setSelectedVariant(v);
                            setSelectedSize(null);
                          }}
                          className={`group relative p-1 rounded-full border-2 transition-all ${selectedVariant?.variantId === v.variantId
                              ? 'scale-110'
                              : 'border-transparent hover:border-gray-200'
                            }`}
                          style={{
                            borderColor:
                              selectedVariant?.variantId === v.variantId
                                ? theme.primary
                                : 'transparent'
                          }}
                        >
                          <div
                            className="w-8 h-8 rounded-full border border-black/5 shadow-inner"
                            style={{
                              background:
                                v.color?.hex || v.color?.hexCode || v.colorHex
                            }}
                          />
                          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none">
                            {v.variantName}
                          </div>
                        </button>
                      ));
                    })()}
                  </div>
                </div>

                {/* اختيار المقاس */}
                {selectedVariant && (
                  <div className="mb-6 animate-in slide-in-from-top-2 duration-300">
                    <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-widest">2. اختر المقاس</label>
                    <div className="flex flex-wrap gap-2">
                      {(selectedVariant.options || []).map(opt => {
                        const isOutOfStock = getOptionStock(opt) <= 0;
                        return (
                          <button
                            key={opt.label}
                            disabled={isOutOfStock}
                            onClick={() => setSelectedSize(opt)}
                            className={`min-w-[50px] px-3 py-2 rounded-xl border-2 font-bold text-sm transition-all ${selectedSize?.label === opt.label
                              ? 'text-white'
                              : isOutOfStock ? 'bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-gray-400'
                              }`}
                            style={selectedSize?.label === opt.label ? { background: theme.primary, borderColor: theme.primary } : {}}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* اختيار الكمية */}
                {selectedSize && (
                  <div className="mb-8 animate-in slide-in-from-top-2 duration-300">
                    <label className="block text-xs font-bold text-gray-400 mb-2 uppercase tracking-widest">3. الكمية</label>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center bg-gray-100 rounded-2xl p-1">
                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 flex items-center justify-center font-bold text-xl hover:bg-white rounded-xl transition-all" style={{ flexShrink: 0 }}>−</button>
                        <span className="min-w-[40px] flex items-center justify-center font-black text-lg">{qty}</span>
                        <button
                          onClick={() => {
                            if (selectedSize && qty < getOptionStock(selectedSize)) {
                              setQty(qty + 1);
                            }
                          }}
                          disabled={selectedSize && qty >= getOptionStock(selectedSize)}
                          className="w-10 h-10 flex items-center justify-center font-bold text-xl hover:bg-white rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ flexShrink: 0 }}
                        >
                          +
                        </button>
                      </div>
                      <div className="text-xs text-gray-400">المتوفر: {getOptionStock(selectedSize)} قطعة</div>
                    </div>
                  </div>
                )}

                {/* ====== باقي كود اختيار اللون والمقاس والكمية كما هو عندك ====== */}
                {/* انسخ نفس الكود الموجود عندك هنا بدون أي تعديل */}
              </div>

              {/* Sticky Button */}
              <div className="p-6 border-t border-gray-100 bg-white">
                <button
                  disabled={!selectedSize || getOptionStock(selectedSize) <= 0}
                  onClick={handleAddSelection}
                  className="w-full py-6 rounded-[2rem] text-white font-black text-xl shadow-2xl hover:scale-[1.01] active:scale-95 disabled:opacity-50 transition-all"
                  style={{
                    background: theme.primary,
                    boxShadow: `${theme.primary}4d 0px 20px 40px`
                  }}
                >
                  تحديث الطلب
                </button>
              </div>
            </div>,
            document.body
          )
        }

        <div className="edit-header" style={{ borderBottom: '1px solid #f0f0f0', padding: isMobile ? '14px 18px' : '20px 30px', position: isMobile ? 'sticky' : 'static', top: 0, background: '#fff', zIndex: 8 }}>
          <h3 style={{ fontSize: isMobile ? '18px' : '24px', fontWeight: '800' }}>تعديل الطلب #{draft.orderNo}</h3>
          <button onClick={onCancel} style={{ background: '#f5f5f5', border: 'none', width: '38px', height: '38px', borderRadius: '50%', cursor: 'pointer', fontSize: '22px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, paddingBottom: '4px' }}>×</button>
        </div>

        <div className="edit-info-box" style={{ background: '#f9fafb', margin: isMobile ? '12px 18px' : '0 30px', borderRadius: '20px', border: '1px solid #f0f0f0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', textAlign: 'right', padding: isMobile ? '16px' : '20px' }}>
          <div style={{ opacity: 0.7 }}>العميل: <b style={{ color: '#000' }}>{draft.customerName}</b></div>
          <div style={{ opacity: 0.7 }}>الهاتف: <b style={{ color: '#000' }}>{draft.customerPhone1}</b></div>
          <div style={{ opacity: 0.7 }}>المحافظة: <b style={{ color: '#000' }}>{draft.city}</b></div>
          <div style={{ opacity: 0.7 }}>العنوان: <b style={{ color: '#000' }}>{draft.address}</b></div>
          <div style={{ opacity: 0.7 }}>الملاحظات: <b style={{ color: '#000' }}>{draft.notes || draft.Notes || "-"}</b></div>
        </div>

        <div className="edit-body" style={{ gap: '0', padding: '0' }}>

          {/* الجهه اليمنى: المعرض (المنتجات) - RTL: الأهم هو التناظر */}
          {/* الجهه اليمنى: المعرض (المنتجات) */}
          <div className="edit-right" style={{
            flex: '1.2',
            borderLeft: '1px solid #f0f0f0',
            padding: isMobile ? '16px 18px' : '24px 30px',
            background: '#fcfcfc',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h4 style={{ color: '#111', fontSize: '15px', fontWeight: '900' }}>المعرض المتاح</h4>
              <span style={{ fontSize: '11px', background: '#eee', padding: '2px 8px', borderRadius: '10px', color: '#666' }}>
                {products?.length || 0} منتج
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              {(!products || products.length === 0) ? (
                <div style={{ padding: '60px 0', textAlign: 'center', color: '#bbb' }}>جاري جلب المنتجات...</div>
              ) : (
                products.map(p => {
                  // 1. منطق تجميع الألوان لمنع التكرار (نفس منطق ProductCard)
                  const colorGroups = {};
                  (p.variants || []).forEach(v => {
                    const hex = v.color?.hex || v.color?.hexCode || "#333";
                    if (!colorGroups[hex]) {
                      colorGroups[hex] = v; // نأخذ أول فاريانت يمثل هذا اللون
                    }
                  });
                  const uniqueVariantsCount = Object.keys(colorGroups).length;

                  // 2. منطق حساب السعر بعد الخصم (نفس منطق ProductCard)
                  // نأخذ أول فاريانت متاح للحساب الافتراضي للسعر في المعرض
                  const firstVariant = p.variants?.[0];
                  let finalDisplayPrice = 0;

                  if (firstVariant) {
                    const basePrice = Number(firstVariant.price || 0);
                    const preCalculatedDiscount = Number(firstVariant.discount || 0);
                    const preCalculatedFinal = Number(firstVariant.finalPrice || (basePrice - preCalculatedDiscount));

                    const dValue = Number(p.discountValue || 0);
                    const dType = Number(p.discountType || 0);

                    finalDisplayPrice = preCalculatedFinal;

                    // إذا لم يكن هناك خصم محسوب مسبقاً، نحسبه من خصم المنتج العام
                    if (preCalculatedDiscount === 0 && dValue > 0) {
                      let discountAmount = dType === 1 ? (basePrice * dValue) / 100 : dValue;
                      finalDisplayPrice = basePrice - discountAmount;
                    }
                  } else {
                    finalDisplayPrice = Number(p.price || 0);
                  }

                  return (
                    <div
                      key={p.id}
                      className="edit-add-card"
                      onClick={() => {
                        setSelectedProduct(p);
                        if (p.variants?.length > 0) setSelectedVariant(p.variants[0]);
                      }}
                      style={{
                        background: '#fff',
                        border: '1px solid #f0f0f0',
                        borderRadius: '20px',
                        padding: '12px',
                        display: 'flex',
                        gap: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.02)'
                      }}
                    >
                      <div style={{ width: '60px', height: '60px', background: '#f5f5f5', borderRadius: '14px', flexShrink: 0, overflow: 'hidden' }}>
                        <img
                          src={buildImageUrl(p.mainImage)}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            padding: 0,
                            borderRadius: 12,
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '800', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: '10px', color: '#999', marginTop: '2px', height: '14px' }}>
                          {uniqueVariantsCount} ألوان متاحة
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '5px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '900', color: theme.primary }}>
                            {formatNumber(finalDisplayPrice)} IQD
                            <span style={{ fontSize: '9px', fontWeight: 'normal', color: '#aaa', marginRight: '4px' }}>بعد الخصم</span>
                          </div>
                          <div style={{ width: '24px', height: '24px', borderRadius: '8px', border: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>←</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* الجهه اليسرى: المنتجات الحالية */}
          <div className="edit-left" style={{ flex: '2', padding: isMobile ? '16px 18px' : '24px 30px', overflowY: 'auto' }}>
            <h4 style={{ marginBottom: '16px', color: '#111', fontSize: '15px', fontWeight: '900' }}>محتويات الطلب الحالية</h4>

            {(!draft.details || draft.details.length === 0) ? (
              <div style={{ textAlign: 'center', padding: '60px 0', border: '2px dashed #f0f0f0', borderRadius: '30px', color: '#ccc' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>🛒</div>
                <span>الطلب فارغ حالياً</span>
              </div>
            ) : (
              draft.details.map((item, i) => (
                <div key={`${item.variantId}-${i}`} className="edit-item-card" style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: '24px', padding: isMobile ? '14px' : '20px', transition: 'all 0.3s' }}>
                  {(() => {
                    const itemStock = resolveItemStock(item);
                    const reachedMax = Number(item.quantity) >= itemStock;
                    return (
                      <>
                        <img className="edit-item-img" src={buildImageUrl(item.imageUrl)} style={{ width: isMobile ? '70px' : '90px', height: isMobile ? '70px' : '90px', borderRadius: '18px', border: '1px solid #f8f8f8' }} />
                        <div className="edit-item-info" style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <div>
                              <b style={{ fontSize: isMobile ? '15px' : '17px' }}>{item.itemName}</b>
                              <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#aaa', marginTop: '2px' }}>{item.variantName} {item.size ? `- مقاس: ${item.size}` : ''}</div>
                            </div>
                            {!locked && (
                              <button
                                onClick={() => setDraft(d => ({ ...d, details: d.details.filter((_, idx) => idx !== i) }))}
                                style={{ color: '#ccc', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}
                                onMouseOver={e => e.target.style.color = '#ff4d4f'}
                                onMouseOut={e => e.target.style.color = '#ccc'}
                              >✕</button>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginTop: '12px', gap: isMobile ? '10px' : '0' }}>
                            <div className="edit-qty-row" style={{ background: '#f8f8f8', borderRadius: '16px', padding: '5px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                              <button disabled={locked} onClick={() => updateQty(i, -1)} style={{ background: '#fff', borderRadius: '12px', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>−</button>
                              <span style={{ minWidth: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '15px' }}>{item.quantity}</span>
                              <button
                                disabled={locked || reachedMax || itemStock <= 0}
                                onClick={() => updateQty(i, 1)}
                                style={{
                                  background: '#fff',
                                  borderRadius: '12px',
                                  width: '30px',
                                  height: '30px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  cursor: (locked || reachedMax || itemStock <= 0) ? 'not-allowed' : 'pointer',
                                  opacity: (locked || reachedMax || itemStock <= 0) ? 0.5 : 1
                                }}
                              >
                                +
                              </button>
                            </div>
                            <div className="edit-line-total" style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '900', color: theme.primary }}>
                              {formatNumber(item.lineTotal || item.price * item.quantity)} <span style={{ fontSize: '11px', fontWeight: 'normal' }}>IQD</span>
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ))
            )}
          </div>

        </div>

        <div className="edit-footer" style={{ borderTop: '1px solid #f0f0f0', padding: isMobile ? '14px 18px' : '24px 30px', background: '#fff', position: isMobile ? 'sticky' : 'static', bottom: 0, zIndex: 8 }}>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <span style={{ color: '#999', fontSize: '12px' }}>إجمالي الطلب:</span>
            <div style={{ fontSize: '24px', fontWeight: '900', color: '#111' }}>{formatNumber(draft.details.reduce((s, i) => s + Number(i.lineTotal), 0))} IQD</div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onCancel} style={{ background: '#fff', color: theme.primary, borderRadius: '18px', padding: '15px 35px', border: `1px solid ${theme.primary}` }}>إلغاء</button>
            {!locked && (
              <button
                onClick={() => onSave(prepareOrder())}
                className="hover-glow"
                style={{ background: theme.primary, color: '#fff', borderRadius: '18px', padding: '15px 60px', boxShadow: `0 10px 25px ${theme.primary}40`, border: 'none' }}
              >
                <span>حفظ</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .hover-glow:hover { transform: translateY(-2px); box-shadow: 0 15px 35px rgba(138, 75, 45, 0.4) !important; transition: all 0.3s ease; }
        .edit-item-card:hover { transform: translateX(-5px); border-color: #8a4b2d44 !important; }
        .edit-overlay::-webkit-scrollbar,
        .edit-body::-webkit-scrollbar {
          width: 6px;
        }
        .edit-overlay::-webkit-scrollbar-track,
        .edit-body::-webkit-scrollbar-track {
          background: #f2f2f2;
        }
        .edit-overlay::-webkit-scrollbar-thumb,
        .edit-body::-webkit-scrollbar-thumb {
          background: ${theme.primary};
          border-radius: 10px;
        }
        .edit-overlay::-webkit-scrollbar-thumb:hover,
        .edit-body::-webkit-scrollbar-thumb:hover {
          background: ${theme.primary}cc;
        }
        .edit-overlay,
        .edit-body {
          scrollbar-color: ${theme.primary} #f2f2f2;
          scrollbar-width: thin;
        }
      `}</style>
    </div >
  );
}

